import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ExternalLink } from "lucide-react";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const DESKTOP = "(min-width: 768px)";
const useMedia = (q) => {
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.matchMedia(q).matches : true));
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [q]);
  return m;
};

const Count = ({ n, tone }) =>
  n > 0 ? (
    <span className={cn("rounded bg-muted px-1.5 font-mono text-[10.5px] tabular leading-4 text-foreground/70", tone === "danger" && "bg-rose-500/10 text-rose-700 dark:text-rose-300", tone === "warning" && "bg-amber-500/10 text-amber-700 dark:text-amber-300")}>
      {n}
    </span>
  ) : null;

/* label pill – hover surface lives here so the sliding underline can span the whole trigger */
const pill = "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] font-medium transition-colors duration-150 group-hover:bg-muted/70 group-hover:text-foreground group-focus-visible:bg-muted/70 group-focus-visible:text-foreground";

/**
 * Prosjektside sub-nav (2026): one sticky row directly under the app topbar.
 * – clusters (Plan · Leveranse · Drift · Arkiv) separated by hairlines; `?tab=` keys unchanged
 * – sliding active underline (transform only), counts only when > 0
 * – md+: priority+ overflow → tabs that do not fit move into «Mer» (grouped); an active overflowed tab
 *   is promoted to the «Mer» button label
 * – < md: horizontal scroll with snap + edge fades; the active tab is centred
 * – keyboard: Radix roving focus (←/→/Home/End)
 */
export const Subnav = ({ groups, value, onSelect, onLink, stuck, anchorRef, className, idPrefix = "project", ariaLabel = "Faner" }) => {
  const tid = (suffix) => `${idPrefix}-${suffix}`;
  const desktop = useMedia(DESKTOP);
  const items = useMemo(() => groups.flatMap((g) => g.tabs.filter((t) => !t.hidden).map((t) => ({ ...t, group: g.key, groupLabel: g.label }))), [groups]);
  const measureKey = items.map((i) => `${i.key}:${i.label}:${i.count || 0}:${i.href ? 1 : 0}`).join("|");
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const listRef = useRef(null);
  const moreRef = useRef(null);
  const itemRefs = useRef({});
  const widths = useRef({});
  const [visible, setVisible] = useState(Infinity);
  const [ind, setInd] = useState({ x: 0, w: 0, ready: false });
  const [edges, setEdges] = useState({ start: true, end: true });

  /* ---- priority+ (desktop only) ---- */
  const compute = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    if (!window.matchMedia(DESKTOP).matches) { setVisible(Infinity); return; }
    const its = itemsRef.current;
    for (const it of its) { const el = itemRefs.current[it.key]; if (el) widths.current[it.key] = el.offsetWidth; }
    if (its.some((it) => widths.current[it.key] == null)) { setVisible(Infinity); return; } // render all → measure next pass
    const cs = getComputedStyle(list);
    const gap = parseFloat(cs.columnGap) || 0;
    const avail = list.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
    const sep = list.querySelector("[data-subnav-sep]");
    const sepBox = sep ? sep.offsetWidth + (parseFloat(getComputedStyle(sep).marginLeft) || 0) + (parseFloat(getComputedStyle(sep).marginRight) || 0) : 17;
    const fit = (budget) => {
      let used = 0, n = 0, last = null;
      for (const it of its) {
        let add = 0;
        if (it.group !== last) { if (last !== null) add += sepBox + gap; last = it.group; }
        add += widths.current[it.key] + gap;
        if (used + add - gap > budget) break;
        used += add; n += 1;
      }
      return n;
    };
    let n = fit(avail);
    if (n < its.length) n = fit(avail - (moreRef.current?.offsetWidth || 0) - gap);
    setVisible(n >= its.length ? Infinity : n);
  }, []);

  useLayoutEffect(() => { widths.current = {}; setVisible(Infinity); }, [measureKey]);
  useLayoutEffect(() => { compute(); }, [compute, visible, measureKey, desktop]);
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => compute());
    ro.observe(list);
    document.fonts?.ready?.then(() => compute());
    return () => ro.disconnect();
  }, [compute]);

  const shown = visible === Infinity ? items : items.slice(0, visible);
  const overflow = visible === Infinity ? [] : items.slice(visible);
  const overflowActive = overflow.find((t) => t.key === value);

  /* ---- sliding indicator ---- */
  const place = useCallback(() => {
    const el = itemRefs.current[value];
    if (!el || !listRef.current) { setInd((s) => ({ ...s, w: 0 })); return; }
    setInd({ x: el.offsetLeft, w: el.offsetWidth, ready: true });
  }, [value]);
  useLayoutEffect(() => { place(); }, [place, visible, measureKey, desktop]);
  useEffect(() => {
    const el = itemRefs.current[value];
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => place());
    ro.observe(el);
    return () => ro.disconnect();
  }, [place, value, visible]);

  /* ---- mobile: keep the active tab in view + edge fades ---- */
  const syncEdges = useCallback(() => {
    const l = listRef.current;
    if (!l) return;
    setEdges({ start: l.scrollLeft <= 1, end: l.scrollLeft + l.clientWidth >= l.scrollWidth - 1 });
  }, []);
  useEffect(() => {
    if (desktop) return;
    const l = listRef.current, el = itemRefs.current[value];
    if (l && el) l.scrollTo({ left: Math.max(0, el.offsetLeft - (l.clientWidth - el.offsetWidth) / 2), behavior: "smooth" });
    syncEdges();
  }, [value, desktop, syncEdges]);
  useEffect(() => { syncEdges(); }, [syncEdges, measureKey, desktop]);

  const mask = !desktop && !(edges.start && edges.end)
    ? `linear-gradient(to right, ${edges.start ? "#000" : "transparent"}, #000 28px, #000 calc(100% - 28px), ${edges.end ? "#000" : "transparent"})`
    : undefined;

  let lastGroup = null;
  return (
    <>
      <div ref={anchorRef} className="subnav-anchor h-px -mt-px scroll-mt-[var(--subnav-offset)]" aria-hidden data-testid={tid("subnav-anchor")} />
      <div
        className={cn(
          "subnav-sticky sticky top-[var(--subnav-offset)] z-20 -mx-5 border-b border-border/60 bg-background transition-shadow duration-200 supports-[backdrop-filter]:bg-background/95 supports-[backdrop-filter]:backdrop-blur-lg sm:-mx-6 lg:-mx-8",
          stuck && "shadow-[0_8px_24px_-16px_rgba(16,24,40,0.18)] dark:shadow-[0_8px_24px_-16px_rgba(0,0,0,0.6)]",
          className
        )}
        data-testid={tid("subnav")}
        data-stuck={stuck || undefined}
      >
        <TabsList
          ref={listRef}
          onScroll={desktop ? undefined : syncEdges}
          style={mask ? { WebkitMaskImage: mask, maskImage: mask } : undefined}
          className="relative flex h-11 w-full items-stretch gap-1 overflow-x-auto border-0 px-5 text-muted-foreground snap-x scroll-px-5 [scrollbar-width:none] sm:px-6 md:overflow-hidden lg:px-8 [&::-webkit-scrollbar]:hidden"
          data-testid={tid("detail-tabs")}
          aria-label={ariaLabel}
        >
          {groups.map((g) => {
            const inG = shown.filter((t) => t.group === g.key);
            if (!inG.length) return null;
            const sep = lastGroup !== null;
            lastGroup = g.key;
            return (
              /* display:contents – keeps the group wrapper (tests/semantics) without a layout box */
              <div key={g.key} className="contents" data-testid={tid(`tab-group-${g.key}`)}>
                {sep && <span aria-hidden data-subnav-sep className="mx-2 h-4 w-px shrink-0 self-center bg-border" />}
                {inG.map((t) =>
                  t.href ? (
                    <button key={t.key} type="button" ref={(el) => { itemRefs.current[t.key] = el; }} onClick={() => onLink(t.href)} data-testid={t.testId || tid(`tab-${t.key}`)}
                      className="group relative inline-flex h-11 shrink-0 snap-start items-center px-0.5 text-muted-foreground focus-visible:outline-none">
                      <span className={pill}>{t.label} <ExternalLink className="h-3 w-3 opacity-70" /></span>
                    </button>
                  ) : (
                    <TabsTrigger key={t.key} value={t.key} ref={(el) => { itemRefs.current[t.key] = el; }} data-testid={t.testId || tid(`tab-${t.key}`)}
                      className="group relative mb-0 h-11 shrink-0 snap-start border-0 px-0.5 data-[state=active]:border-transparent">
                      <span className={pill}>{t.label}<Count n={t.count} tone={t.tone} /></span>
                    </TabsTrigger>
                  )
                )}
              </div>
            );
          })}

          {/* «Mer» – always in the DOM for measurement; visible only when something overflows */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" ref={moreRef} data-testid={tid("tabs-more")} aria-label="Flere faner"
                className={cn("group relative inline-flex h-11 shrink-0 items-center px-0.5 focus-visible:outline-none", overflow.length ? (overflowActive ? "text-foreground" : "text-muted-foreground") : "invisible absolute right-0")}>
                <span aria-hidden className="mx-2 h-4 w-px self-center bg-border" />
                <span className={pill}>{overflowActive ? overflowActive.label : "Mer"}{overflowActive && <Count n={overflowActive.count} tone={overflowActive.tone} />}<ChevronDown className="h-3.5 w-3.5 opacity-70" /></span>
                {overflowActive && <span aria-hidden className="absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-primary" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid={tid("tabs-more-content")}>
              {groups.filter((g) => overflow.some((t) => t.group === g.key)).map((g, gi) => (
                <div key={g.key}>
                  {gi > 0 && <DropdownMenuSeparator />}
                  <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground">{g.label}</DropdownMenuLabel>
                  {overflow.filter((t) => t.group === g.key).map((t) => (
                    <DropdownMenuItem key={t.key} onSelect={() => (t.href ? onLink(t.href) : onSelect(t.key))} data-testid={t.testId || tid(`tab-${t.key}`)} className={cn(t.key === value && "font-medium text-foreground")}>
                      {t.label}{t.href && <ExternalLink className="h-3 w-3 opacity-70" />}
                      <span className="ml-auto"><Count n={t.count} tone={t.tone} /></span>
                    </DropdownMenuItem>
                  ))}
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <span aria-hidden data-testid={tid("subnav-indicator")}
            className={cn("pointer-events-none absolute bottom-0 left-0 h-[2px] w-px origin-left rounded-full bg-primary", ind.ready && "transition-transform duration-200 ease-out motion-reduce:transition-none")}
            style={{ transform: `translateX(${ind.x}px) scaleX(${ind.w})` }} />
        </TabsList>
      </div>
    </>
  );
};

/** Project detail flavour – keeps the `project-*` test ids. */
export const ProjectSubnav = (props) => <Subnav idPrefix="project" ariaLabel="Prosjektfaner" {...props} />;
